import {
  amountToMinorFractionalUnits,
  minorFractionalUnitsToAmount,
} from "@redotech/money/iso4217/currencies";
import { getPlainDateFromDateUtc } from "@redotech/util/date";
import { DEFAULT_RULE_OPTIMIZATION, DEFAULT_RULE_URGENCIES } from "./constants";
import {
  type DraftPOCandidate,
  type DraftPOGroup,
  type DraftPOGrouping,
  type ExistingRecommendationForGeneration,
  type GenerationSupplierOption,
  type NormalizedRecommendationSchedule,
  type PlannedRecommendationCreate,
  type RecommendationGenerationCandidate,
  type RecommendationGenerationPlan,
  type RecommendationGenerationRule,
  type RecommendationGenerationVariant,
  type RecommendationOptimization,
  type RecommendationScheduleFields,
  type RecommendationSelectionCandidate,
  type RecommendationUrgency,
} from "./types";

/**
 * Recommendation math
 */

export function computeRiskPerDayFractional(
  candidate: Pick<
    RecommendationGenerationCandidate,
    "risk_units_per_day" | "unit_cost_fractional"
  >,
): bigint | null {
  if (
    candidate.risk_units_per_day == null ||
    candidate.unit_cost_fractional == null
  ) {
    return null;
  }
  return candidate.risk_units_per_day * candidate.unit_cost_fractional;
}

/**
 * Scheduling
 */

export function normalizeRecommendationSchedule({
  scheduleIntervalCount,
  scheduleIntervalUnit,
}: RecommendationScheduleFields): NormalizedRecommendationSchedule {
  if (scheduleIntervalUnit == null) {
    return { scheduleIntervalCount: null, scheduleIntervalUnit: null };
  }
  return {
    scheduleIntervalCount: scheduleIntervalCount ?? 0,
    scheduleIntervalUnit,
  };
}

interface GetNextRecommendationRunAtInput extends RecommendationScheduleFields {
  now: Temporal.ZonedDateTime;
}

export function getNextRecommendationRunAt({
  scheduleIntervalCount,
  scheduleIntervalUnit,
  now,
}: GetNextRecommendationRunAtInput) {
  const schedule = normalizeRecommendationSchedule({
    scheduleIntervalCount,
    scheduleIntervalUnit,
  });

  switch (schedule.scheduleIntervalUnit) {
    case "day":
      return new Date(
        now
          .add({ days: schedule.scheduleIntervalCount })
          .toInstant()
          .toString(),
      );
    case "week":
      return new Date(
        now
          .add({ weeks: schedule.scheduleIntervalCount })
          .toInstant()
          .toString(),
      );
    case "month":
      return new Date(
        now
          .add({ months: schedule.scheduleIntervalCount })
          .toInstant()
          .toString(),
      );
    case null:
      return null;
  }
}

/**
 * Rule runs
 */

export interface PlanRecommendationGenerationInput {
  rule: RecommendationGenerationRule;
  candidates: RecommendationGenerationCandidate[];
  existingRecommendations: ExistingRecommendationForGeneration[];
  today: Temporal.PlainDate;
  now: Temporal.ZonedDateTime;
}

export function planRecommendationGeneration({
  rule,
  candidates,
  existingRecommendations,
  today,
  now,
}: PlanRecommendationGenerationInput): RecommendationGenerationPlan {
  if (!rule.enabled) {
    return {
      candidatesToCreate: [],
      nextRunAt: null,
      recommendationIdsToExpire: [],
      shouldRun: false,
    };
  }

  const selectedCandidates = applyRecommendationRuleCriteria(
    candidates,
    {
      optimization: rule.optimization ?? DEFAULT_RULE_OPTIMIZATION,
      spendCapFractional: rule.spendCapFractional,
      urgencies:
        rule.urgency && rule.urgency.length > 0
          ? rule.urgency
          : DEFAULT_RULE_URGENCIES,
    },
    today,
  );
  const selectedCandidateKeys = new Set(
    selectedCandidates.map((row) =>
      recommendationKey(row.product_id, row.supplier_id),
    ),
  );
  const existingByKey = new Map<
    string,
    ExistingRecommendationForGeneration[]
  >();
  for (const row of existingRecommendations) {
    const key = recommendationKey(row.product_id, row.supplier_id);
    const existing = existingByKey.get(key);
    if (existing) {
      existing.push(row);
      continue;
    }
    existingByKey.set(key, [row]);
  }

  const recommendationIdsToExpire = existingRecommendations
    .filter(
      (row) =>
        row.rule_id === rule.id &&
        !selectedCandidateKeys.has(
          recommendationKey(row.product_id, row.supplier_id),
        ),
    )
    .map((row) => row.id);

  const candidatesToCreate: PlannedRecommendationCreate[] = [];
  for (const candidate of selectedCandidates) {
    const riskPerDayFractional = computeRiskPerDayFractional(candidate);
    const existing =
      existingByKey.get(
        recommendationKey(candidate.product_id, candidate.supplier_id),
      ) ?? [];
    if (
      existing.some((row) =>
        recommendationMatchesCandidate(row, candidate, riskPerDayFractional),
      )
    ) {
      continue;
    }
    candidatesToCreate.push({
      candidate,
      riskPerDayFractional,
      supersededRecommendationIds: existing.map((row) => row.id),
    });
  }

  const nextRunAt = getNextRecommendationRunAt({
    scheduleIntervalCount: rule.scheduleIntervalCount,
    scheduleIntervalUnit: rule.scheduleIntervalUnit,
    now,
  });

  return {
    candidatesToCreate,
    nextRunAt,
    recommendationIdsToExpire,
    shouldRun: true,
  };
}

export interface RecommendationSelectionCriteria {
  optimization: RecommendationOptimization;
  urgencies: Iterable<RecommendationUrgency>;
  spendCapFractional: number | null;
}

export interface SelectRecommendationCandidatesInput<
  TCandidate extends RecommendationSelectionCandidate,
> {
  candidates: TCandidate[];
  criteria: RecommendationSelectionCriteria;
}

export function selectRecommendationCandidates<
  TCandidate extends RecommendationSelectionCandidate,
>({
  candidates,
  criteria,
}: SelectRecommendationCandidatesInput<TCandidate>): TCandidate[] {
  const urgencyList = [...criteria.urgencies];
  const eligibleCandidates = candidates.filter((candidate) => {
    if (!urgencyList.includes(candidate.urgency)) {
      return false;
    }
    return true;
  });

  const supplierTotals = new Map<string, bigint>();
  for (const candidate of eligibleCandidates) {
    supplierTotals.set(
      candidate.supplierId,
      (supplierTotals.get(candidate.supplierId) ?? 0n) +
        recommendationSelectionLineCostFractional(candidate),
    );
  }

  const orderedCandidates = [...eligibleCandidates].sort((a, b) => {
    switch (criteria.optimization) {
      case "fewest_suppliers": {
        const aSupplierTotal = supplierTotals.get(a.supplierId) ?? 0n;
        const bSupplierTotal = supplierTotals.get(b.supplierId) ?? 0n;
        if (aSupplierTotal !== bSupplierTotal) {
          return aSupplierTotal > bSupplierTotal ? -1 : 1;
        }
        return a.supplierName.localeCompare(b.supplierName);
      }
      case "balanced": {
        const aLineCost = recommendationSelectionLineCostFractional(a);
        const bLineCost = recommendationSelectionLineCostFractional(b);
        if (aLineCost !== bLineCost) {
          return aLineCost < bLineCost ? -1 : 1;
        }
        const aSupplierTotal = supplierTotals.get(a.supplierId) ?? 0n;
        const bSupplierTotal = supplierTotals.get(b.supplierId) ?? 0n;
        if (aSupplierTotal !== bSupplierTotal) {
          return aSupplierTotal > bSupplierTotal ? -1 : 1;
        }
        return a.supplierName.localeCompare(b.supplierName);
      }
      case "lowest_cost": {
        const aLineCost = recommendationSelectionLineCostFractional(a);
        const bLineCost = recommendationSelectionLineCostFractional(b);
        if (aLineCost === bLineCost) {
          return 0;
        }
        return aLineCost < bLineCost ? -1 : 1;
      }
      case "fastest_stockout": {
        if (a.projectedStockoutDate && b.projectedStockoutDate) {
          const stockoutDelta = Temporal.PlainDate.compare(
            a.projectedStockoutDate,
            b.projectedStockoutDate,
          );
          if (stockoutDelta !== 0) {
            return stockoutDelta;
          }
        } else if (a.projectedStockoutDate || b.projectedStockoutDate) {
          return a.projectedStockoutDate ? -1 : 1;
        }

        if (a.orderByDate && b.orderByDate) {
          return Temporal.PlainDate.compare(a.orderByDate, b.orderByDate);
        }
        if (a.orderByDate || b.orderByDate) {
          return a.orderByDate ? -1 : 1;
        }
        return 0;
      }
    }
  });

  if (criteria.spendCapFractional == null) {
    return orderedCandidates;
  }

  const spendCapFractionalBigInt = BigInt(criteria.spendCapFractional);
  let selectedTotalFractional = 0n;
  const selectedCandidates: TCandidate[] = [];
  for (const candidate of orderedCandidates) {
    const lineCost = recommendationSelectionLineCostFractional(candidate);
    if (selectedTotalFractional + lineCost > spendCapFractionalBigInt) {
      continue;
    }
    selectedCandidates.push(candidate);
    selectedTotalFractional += lineCost;
  }
  return selectedCandidates;
}

interface RuleSelectionCandidate extends RecommendationSelectionCandidate {
  candidate: RecommendationGenerationCandidate;
}

export function applyRecommendationRuleCriteria(
  candidates: RecommendationGenerationCandidate[],
  criteria: RecommendationSelectionCriteria,
  today: Temporal.PlainDate,
) {
  const selectionCandidates: RuleSelectionCandidate[] = candidates.map(
    (candidate) => {
      const orderByDate = getPlainDateFromDateUtc(candidate.order_by_date);
      return {
        candidate,
        currency: candidate.currency,
        orderByDate,
        projectedStockoutDate: getPlainDateFromDateUtc(
          candidate.projected_stockout_date,
        ),
        suggestedQuantity: candidate.suggested_quantity,
        supplierId: candidate.supplier_id.toString(),
        supplierName: candidate.supplier_name,
        unitCostFractional: candidate.unit_cost_fractional,
        urgency: classifyRecommendationUrgency({ orderByDate, today }),
      };
    },
  );

  return selectRecommendationCandidates({
    candidates: selectionCandidates,
    criteria,
  }).map((candidate) => candidate.candidate);
}

/**
 * Open purchase order surfacing
 */

export interface OpenPurchaseOrderLine {
  productId: bigint;
  quantity: bigint;
  isDraft: boolean;
  createdAt: Temporal.Instant;
}

export interface ProductOpenPoContext {
  totalOnOrder: bigint;
  latestDraftCreatedAt: Temporal.Instant | null;
}

export function summarizeOpenPurchaseOrdersByProduct(
  lines: OpenPurchaseOrderLine[],
): Map<string, ProductOpenPoContext> {
  const contextByProduct = new Map<string, ProductOpenPoContext>();
  for (const line of lines) {
    const key = line.productId.toString();
    const context = contextByProduct.get(key) ?? {
      totalOnOrder: 0n,
      latestDraftCreatedAt: null,
    };
    context.totalOnOrder += line.quantity;
    if (
      line.isDraft &&
      (context.latestDraftCreatedAt == null ||
        Temporal.Instant.compare(line.createdAt, context.latestDraftCreatedAt) >
          0)
    ) {
      context.latestDraftCreatedAt = line.createdAt;
    }
    contextByProduct.set(key, context);
  }
  return contextByProduct;
}

const RECENT_DRAFT_SUPPRESSION_DAYS = 7;
const LARGE_ORDER_THRESHOLD = 5;

export interface ShouldSurfaceRecommendationWithOpenPoInput {
  recommendedQuantity: bigint;
  context: ProductOpenPoContext;
  now: Temporal.ZonedDateTime;
}

export function shouldSurfaceRecommendationWithOpenPo({
  recommendedQuantity,
  context,
  now,
}: ShouldSurfaceRecommendationWithOpenPoInput): boolean {
  const { latestDraftCreatedAt, totalOnOrder } = context;
  if (
    latestDraftCreatedAt != null &&
    Temporal.Instant.compare(
      latestDraftCreatedAt,
      now.subtract({ days: RECENT_DRAFT_SUPPRESSION_DAYS }).toInstant(),
    ) > 0
  ) {
    return false;
  }
  const onOrder = Number(totalOnOrder);
  const recommended = Number(recommendedQuantity);
  if (onOrder > LARGE_ORDER_THRESHOLD) {
    return recommended >= 1.5 * onOrder;
  }
  return recommended >= 2 * onOrder;
}

/**
 * Supplier selection
 */

export function selectVariantSuppliers(
  variants: RecommendationGenerationVariant[],
  optimization: RecommendationOptimization,
): RecommendationGenerationCandidate[] {
  const sourceable = variants.filter((variant) => variant.suppliers.length > 0);
  if (optimization === "fewest_suppliers") {
    return selectFewestSuppliers(sourceable);
  }
  return sourceable.map((variant) =>
    toGenerationCandidate(
      variant,
      pickSupplierForVariant(variant, optimization),
    ),
  );
}

function selectFewestSuppliers(
  variants: RecommendationGenerationVariant[],
): RecommendationGenerationCandidate[] {
  const unassigned = new Set(variants);
  const result: RecommendationGenerationCandidate[] = [];

  while (unassigned.size > 0) {
    const coverage = new Map<
      string,
      {
        supplierId: bigint;
        variants: RecommendationGenerationVariant[];
        cost: bigint | null;
      }
    >();
    for (const variant of unassigned) {
      for (const supplier of variant.suppliers) {
        const key = supplier.supplier_id.toString();
        const entry = coverage.get(key) ?? {
          supplierId: supplier.supplier_id,
          variants: [],
          cost: 0n,
        };
        entry.variants.push(variant);
        const lineCost = variantLineCostFractional(variant, supplier);
        entry.cost =
          entry.cost == null || lineCost == null ? null : entry.cost + lineCost;
        coverage.set(key, entry);
      }
    }

    let best: {
      supplierId: bigint;
      variants: RecommendationGenerationVariant[];
      cost: bigint | null;
    } | null = null;
    for (const entry of coverage.values()) {
      if (best == null || preferCoverage(entry, best)) {
        best = entry;
      }
    }
    if (best == null) {
      break;
    }
    const chosen = best;

    for (const variant of chosen.variants) {
      const supplier = variant.suppliers.find(
        (option) => option.supplier_id === chosen.supplierId,
      );
      if (supplier) {
        result.push(toGenerationCandidate(variant, supplier));
      }
      unassigned.delete(variant);
    }
  }

  return result;
}

function pickSupplierForVariant(
  variant: RecommendationGenerationVariant,
  optimization: RecommendationOptimization,
): GenerationSupplierOption {
  if (optimization === "fastest_stockout") {
    return variant.suppliers.reduce((best, next) =>
      shorterLeadTime(next, best) ? next : best,
    );
  }
  return variant.suppliers.reduce((best, next) =>
    lowerCost(
      variantLineCostFractional(variant, next),
      next.supplier_id,
      variantLineCostFractional(variant, best),
      best.supplier_id,
    )
      ? next
      : best,
  );
}

function preferCoverage(
  next: { variants: unknown[]; cost: bigint | null; supplierId: bigint },
  best: { variants: unknown[]; cost: bigint | null; supplierId: bigint },
): boolean {
  if (next.variants.length !== best.variants.length) {
    return next.variants.length > best.variants.length;
  }
  return lowerCost(next.cost, next.supplierId, best.cost, best.supplierId);
}

function lowerCost(
  nextCost: bigint | null,
  nextSupplierId: bigint,
  bestCost: bigint | null,
  bestSupplierId: bigint,
): boolean {
  if (nextCost == null && bestCost == null) {
    return nextSupplierId < bestSupplierId;
  }
  if (nextCost == null) {
    return false;
  }
  if (bestCost == null) {
    return true;
  }
  if (nextCost !== bestCost) {
    return nextCost < bestCost;
  }
  return nextSupplierId < bestSupplierId;
}

function shorterLeadTime(
  next: GenerationSupplierOption,
  best: GenerationSupplierOption,
): boolean {
  if (next.lead_time_days == null && best.lead_time_days == null) {
    return next.supplier_id < best.supplier_id;
  }
  if (next.lead_time_days == null) {
    return false;
  }
  if (best.lead_time_days == null) {
    return true;
  }
  if (next.lead_time_days !== best.lead_time_days) {
    return next.lead_time_days < best.lead_time_days;
  }
  return next.supplier_id < best.supplier_id;
}

function variantLineCostFractional(
  variant: RecommendationGenerationVariant,
  supplier: GenerationSupplierOption,
): bigint | null {
  return supplier.unit_cost_fractional == null
    ? null
    : variant.suggested_quantity * supplier.unit_cost_fractional;
}

function toGenerationCandidate(
  variant: RecommendationGenerationVariant,
  supplier: GenerationSupplierOption,
): RecommendationGenerationCandidate {
  return {
    product_id: variant.product_id,
    product_title: variant.product_title,
    sku: variant.sku,
    product_family_id: variant.product_family_id,
    supplier_id: supplier.supplier_id,
    supplier_name: supplier.supplier_name,
    suggested_quantity: variant.suggested_quantity,
    unit_cost_fractional: supplier.unit_cost_fractional,
    currency: supplier.currency,
    risk_units_per_day: variant.risk_units_per_day,
    order_by_date: variant.order_by_date,
    projected_stockout_date: variant.projected_stockout_date,
  };
}

/**
 * Draft PO grouping
 */

export interface BuildDraftPOGroupsInput<TCandidate extends DraftPOCandidate> {
  candidates: TCandidate[];
  optimization: RecommendationOptimization;
  poGrouping: DraftPOGrouping;
  urgencies: Iterable<RecommendationUrgency>;
  spendCapFractional: number | null;
}

export function buildDraftPOGroups<TCandidate extends DraftPOCandidate>({
  candidates,
  optimization,
  poGrouping,
  urgencies,
  spendCapFractional,
}: BuildDraftPOGroupsInput<TCandidate>): DraftPOGroup<TCandidate>[] {
  const draftableCandidates = candidates.filter(
    (candidate) => candidate.status === "active",
  );
  const selected = selectRecommendationCandidates({
    candidates: draftableCandidates,
    criteria: { optimization, spendCapFractional, urgencies },
  });

  const draftPOGroups = new Map<string, DraftPOGroup<TCandidate>>();
  for (const row of selected) {
    const groupKey = getDraftPOGroupKey({ candidate: row, poGrouping });
    const lineTotalFractional = recommendationSelectionLineCostFractional(row);
    const existing = draftPOGroups.get(groupKey);
    if (existing) {
      existing.variants.push(row);
      existing.estimatedTotal += minorFractionalUnitsToAmount(
        lineTotalFractional,
        row.currency,
      );
      continue;
    }
    draftPOGroups.set(groupKey, {
      groupKey,
      supplierId: row.supplierId,
      supplierName: row.supplierName,
      currency: row.currency,
      estimatedTotal: minorFractionalUnitsToAmount(
        lineTotalFractional,
        row.currency,
      ),
      variants: [row],
    });
  }
  return [...draftPOGroups.values()];
}

interface GetDraftPOGroupKeyInput<TCandidate extends DraftPOCandidate> {
  candidate: TCandidate;
  poGrouping: DraftPOGrouping;
}

function getDraftPOGroupKey<TCandidate extends DraftPOCandidate>({
  candidate,
  poGrouping,
}: GetDraftPOGroupKeyInput<TCandidate>) {
  switch (poGrouping) {
    case "supplier":
      return candidate.supplierId;
  }
}

/**
 * Urgency
 */

const recommendationUrgencyThresholds = [
  { urgency: "critical", daysUntilOrderBy: 3 },
  { urgency: "urgent", daysUntilOrderBy: 7 },
  { urgency: "attention", daysUntilOrderBy: 14 },
] as const satisfies readonly {
  urgency: RecommendationUrgency;
  daysUntilOrderBy: number;
}[];

export function getRecommendationUrgencyThresholds() {
  return recommendationUrgencyThresholds;
}

export function getUrgencyRank(urgency: RecommendationUrgency) {
  switch (urgency) {
    case "overdue":
      return 0;
    case "critical":
      return 1;
    case "urgent":
      return 2;
    case "attention":
      return 3;
    case "healthy":
      return 4;
  }
}

export interface ClassifyRecommendationUrgencyInput {
  orderByDate: Temporal.PlainDate | null;
  today: Temporal.PlainDate;
}

export function classifyRecommendationUrgency({
  orderByDate,
  today,
}: ClassifyRecommendationUrgencyInput): RecommendationUrgency {
  if (!orderByDate) {
    return "healthy";
  }
  if (Temporal.PlainDate.compare(orderByDate, today) < 0) {
    return "overdue";
  }
  for (const threshold of getRecommendationUrgencyThresholds()) {
    if (
      Temporal.PlainDate.compare(
        orderByDate,
        today.add({ days: threshold.daysUntilOrderBy }),
      ) <= 0
    ) {
      return threshold.urgency;
    }
  }
  return "healthy";
}

/**
 * Recommendation matching
 */

interface RecommendationCandidateForMatch
  extends Pick<
    RecommendationGenerationCandidate,
    | "suggested_quantity"
    | "unit_cost_fractional"
    | "currency"
    | "risk_units_per_day"
  > {}

interface ExistingRecommendationForMatch
  extends RecommendationCandidateForMatch {
  risk_per_day_fractional: bigint | null;
}

export function recommendationMatchesCandidate(
  existing: ExistingRecommendationForMatch,
  candidate: RecommendationCandidateForMatch,
  riskPerDay: bigint | null,
) {
  return (
    existing.suggested_quantity === candidate.suggested_quantity &&
    existing.unit_cost_fractional === candidate.unit_cost_fractional &&
    existing.currency === candidate.currency &&
    existing.risk_per_day_fractional === riskPerDay &&
    existing.risk_units_per_day === candidate.risk_units_per_day
  );
}

/**
 * Risk and line totals
 */

type RecommendationGenerationLineCostFields = Pick<
  RecommendationGenerationCandidate,
  "suggested_quantity" | "unit_cost_fractional"
>;

export function candidateLineCostFractional(
  candidate: RecommendationGenerationLineCostFields,
) {
  return candidate.unit_cost_fractional == null
    ? 0n
    : candidate.suggested_quantity * candidate.unit_cost_fractional;
}

type RecommendationSelectionLineCostFields = Pick<
  RecommendationSelectionCandidate,
  "suggestedQuantity" | "unitCostFractional"
>;

function recommendationSelectionLineCostFractional(
  candidate: RecommendationSelectionLineCostFields,
) {
  if (candidate.unitCostFractional == null) {
    return 0n;
  }
  return BigInt(candidate.suggestedQuantity) * candidate.unitCostFractional;
}

/**
 * Money input and formatting
 */

export function parseRecommendationMoneyInputToFractional(
  value: string,
  currency = "USD",
) {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) && parsed >= 0
    ? amountToMinorFractionalUnits(parsed, currency)
    : null;
}

export function hasInvalidRecommendationMoneyInput(value: string) {
  const trimmed = value.trim();
  if (!trimmed) {
    return false;
  }
  const parsed = Number(trimmed);
  return !Number.isFinite(parsed) || parsed < 0;
}

/**
 * Internal helpers
 */

function recommendationKey(productId: bigint, supplierId: bigint) {
  return `${productId.toString()}::${supplierId.toString()}`;
}
